import React, { useEffect, useState } from "react";
import {
  Area,
  ComposedChart,
  PieChart,
  Line,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";
import Dropdown from "../assets/svgIcons/PrimaryDropDown.svg";
import { fetchHomeSummary } from "../apis/reports";
import { fetchTransactionByCode } from "../apis/transaction";
import { displayToast } from "../utils/Toast";
import Loader from "../components/Loader";
import { useErrorHandler } from "../services/ErrorHandler";
import useAutoRefresh from "../hooks/useAutoRefresh";
import "../styles/components/SalesOverview.css";

const REVENUE_DISTRIBUTION_DATA = [
  { name: "Sales", value: 45, color: "#2F80ED" },
  { name: "Production", value: 25, color: "#4FC38A" },
  { name: "Services", value: 15, color: "#F29C38" },
  { name: "Others", value: 15, color: "#7A66D9" },
];

const ChartTitleIcon = ({ type }) => {
  if (type === "sales") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 18.5h16"
          fill="none"
          stroke="#69707d"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M4.5 5.5v13"
          fill="none"
          stroke="#69707d"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M7 15l4.2-4.5 4 3.2 4.3-6"
          fill="none"
          stroke="#1f75e8"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.4 7.7H20v2.6"
          fill="none"
          stroke="#1f75e8"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#27ccb0" opacity="0.18" />
      <path
        d="M6.8 14.6l3.4-3.6 3 2 4.2-5.2"
        fill="none"
        stroke="#27ccb0"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.4 7.8H18v2.6"
        fill="none"
        stroke="#27ccb0"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse cx="7.2" cy="17.1" rx="1.9" ry="0.9" fill="#27ccb0" />
      <ellipse cx="12" cy="17.1" rx="1.9" ry="0.9" fill="#27ccb0" />
      <ellipse cx="16.8" cy="17.1" rx="1.9" ry="0.9" fill="#27ccb0" />
    </svg>
  );
};

const SalesOverview = () => {
  const [loading, setLoading] = useState(false);
  const [lineSeriesData, setLineSeriesData] = useState([]);
  const [chartSource, setChartSource] = useState("SE");
  const [monthRange, setMonthRange] = useState(6);
  const [revenuePeriod, setRevenuePeriod] = useState("this-month");
  const [summary, setSummary] = useState({
    summary_data: [
      { NewCustomers: 0, TotalCustomers: 0, TotalProducts: 0, TotalSales: 0 },
    ],
    sales_report: [],
    top_product_report: [],
  });
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const { handleErrorResponse } = useErrorHandler();

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sourceOptions = [
    { label: "Sales Enquiry", value: "SE" },
    { label: "Work Order", value: "WO" },
  ];

  const monthOptions = [3, 4, 5, 6].map((value) => ({
    label: `Last ${value} Months`,
    value,
  }));

  const revenuePeriodOptions = [
    { label: "This Month", value: "this-month" },
    { label: "1 Month", value: "one-month" },
  ];

  const getDateRange = (months) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(1);
    startDate.setMonth(endDate.getMonth() - (months - 1));

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    };
  };

  const getMonthKeys = (months) => {
    const current = new Date();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return Array.from({ length: months }, (_, idx) => {
      const date = new Date(
        current.getFullYear(),
        current.getMonth() - (months - idx - 1),
        1,
      );
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return { key, label: monthNames[date.getMonth()] };
    });
  };

  const pickDateFromRow = (row) => {
    const keys = [
      "date",
      "enquiry_date",
      "wo_date",
      "order_date",
      "created_at",
      "created_date",
      "updated_at",
      "updated_date",
    ];

    for (const key of keys) {
      if (!row?.[key]) continue;
      const parsed = new Date(row[key]);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    return null;
  };

  const pickAmountFromRow = (row) => {
    const amountKeys = [
      "total_amount",
      "amount",
      "net_amount",
      "grand_total",
      "final_amount",
      "sale_amount",
      "sales_amount",
      "total",
      "total_value",
      "value",
      "basic_amount",
      "taxable_amount",
      "po_amount",
    ];

    for (const key of amountKeys) {
      const rawValue = row?.[key];
      const amount = Number(rawValue);
      if (Number.isFinite(amount) && amount >= 0) {
        return amount;
      }
    }

    return 1;
  };

  const fetchSummaryData = async (months, silent = false) => {
    try {
      const dateRange = getDateRange(months);
      const response = await fetchHomeSummary(dateRange);

      if (response.description || response.message) {
        handleErrorResponse(response.description || response.message);
        if (!silent) displayToast(response.message, "error");
      } else {
        setSummary({
          summary_data: response.summary_data || [],
          sales_report: response.sales_report || [],
          top_product_report: response.top_product_report || [],
        });
      }
    } catch (error) {
      if (!silent) displayToast("Failed to fetch sales data", "error");
    }
  };

  const fetchLineSeriesData = async (sourceCode, months) => {
    const dateRange = getDateRange(months);
    const monthBuckets = getMonthKeys(months);
    const bucketMap = monthBuckets.reduce((acc, bucket) => {
      acc[bucket.key] = 0;
      return acc;
    }, {});

    const firstResponse = await fetchTransactionByCode({
      transactionCode: sourceCode,
      page: 1,
      pageSize: 1,
      fromDate: dateRange.start_date,
      toDate: dateRange.end_date,
    });

    if (firstResponse?.description) {
      throw new Error(firstResponse.description);
    }

    const totalCount = Number(
      firstResponse?.TotalCount ??
        firstResponse?.total_count ??
        firstResponse?.totalCount ??
        firstResponse?.count ??
        0,
    );

    const shouldRefetch = Number.isFinite(totalCount) && totalCount > 1;
    const finalResponse = shouldRefetch
      ? await fetchTransactionByCode({
          transactionCode: sourceCode,
          page: 1,
          pageSize: totalCount,
          fromDate: dateRange.start_date,
          toDate: dateRange.end_date,
        })
      : firstResponse;

    const rows = Array.isArray(finalResponse?.TransactionList)
      ? finalResponse.TransactionList
      : [];

    rows.forEach((row) => {
      const rowDate = pickDateFromRow(row);
      if (!rowDate) return;

      const monthKey = `${rowDate.getFullYear()}-${String(rowDate.getMonth() + 1).padStart(2, "0")}`;
      if (!(monthKey in bucketMap)) return;

      bucketMap[monthKey] += pickAmountFromRow(row);
    });

    const chartRows = monthBuckets.map((bucket) => ({
      x_axis: bucket.key,
      y_axis: Number(bucketMap[bucket.key] || 0),
    }));

    setLineSeriesData(chartRows);
  };

  const refreshDashboardData = async (sourceCode, months, silent = false) => {
    try {
      if (!silent) setLoading(true);
      await Promise.all([
        fetchSummaryData(months, silent),
        fetchLineSeriesData(sourceCode, months),
      ]);
    } catch (error) {
      if (!silent) displayToast("Failed to fetch sales overview", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refreshDashboardData(chartSource, monthRange);
  }, [chartSource, monthRange]);

  useAutoRefresh(() =>
    refreshDashboardData(chartSource, monthRange, true),
  );

  const formatMonthLabel = (dateString) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length >= 2) {
      const month = parseInt(parts[1], 10);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${monthNames[month - 1]}`;
    }
    return dateString;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: screenWidth < 576 ? "6px" : "10px",
            border: "1px solid #ccc",
            fontSize: screenWidth < 576 ? "10px" : "14px",
          }}
        >
          <p>{`${label}`}</p>
          <p>{`Sale Amount : ${new Intl.NumberFormat().format(payload[0].value)}`}</p>
          {payload[1] && (
            <p>{`Value : ${new Intl.NumberFormat().format(payload[1].value)}`}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const CenteredYAxisLabel = ({ viewBox }) => {
    if (!viewBox) return null;

    const labelX = viewBox.x - (screenWidth < 576 ? 2 : 6);
    const labelY = viewBox.y + viewBox.height / 2;

    return (
      <text
        x={labelX}
        y={labelY}
        transform={`rotate(-90, ${labelX}, ${labelY})`}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#111111"
        fontSize={screenWidth < 576 ? 8 : screenWidth < 768 ? 10 : 12}
      >
        Sales Amount
      </text>
    );
  };

  const chartLineData =
    lineSeriesData && lineSeriesData.length > 0
      ? lineSeriesData
      : getMonthKeys(monthRange).map((item) => ({
          x_axis: item.key,
          y_axis: 0,
        }));

  const lineValues = chartLineData.map((item) => Number(item.y_axis || 0));

  const getNiceYAxisTicks = (values) => {
    const maxValue = Math.max(...values, 0);
    if (maxValue <= 0) {
      return [0, 10, 20, 30, 40, 50];
    }

    const roughStep = maxValue / 5;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep || 1));
    const normalized = roughStep / magnitude;

    let stepMultiplier = 10;
    if (normalized <= 1) stepMultiplier = 1;
    else if (normalized <= 2) stepMultiplier = 2;
    else if (normalized <= 5) stepMultiplier = 5;

    const step = stepMultiplier * magnitude;
    const maxTick = Math.ceil(maxValue / step) * step;

    return Array.from(
      { length: Math.floor(maxTick / step) + 1 },
      (_, index) => index * step,
    );
  };

  const yAxisTicks = getNiceYAxisTicks(lineValues);
  const yAxisMax = yAxisTicks[yAxisTicks.length - 1] || 50;

  const hasData =
    lineSeriesData &&
    lineSeriesData.length > 0 &&
    lineSeriesData.some((item) => Number(item.y_axis || 0) > 0);

  return (
    <div className="content-containerr">
      {loading ? (
        <Loader />
      ) : (
        <div className="sales-dashboard charts-only">
          <div className="charts-column">
            <div className="chart-card line-chart">
              <div className="chart-head">
                <div className="chart-title-wrap">
                  <span className="chart-title-icon sales-icon">
                    <ChartTitleIcon type="sales" />
                  </span>
                  <h3 className="chart-main-title">Sales Overview</h3>
                </div>
                <div className="sales-overview-controls">
                  <div className="dropdown-container compact">
                    <div
                      className="custom-select-wrapper compact"
                      onClick={() =>
                        document.getElementById("chart-source-select").click()
                      }
                    >
                      <select
                        id="chart-source-select"
                        value={chartSource}
                        onChange={(e) => setChartSource(e.target.value)}
                        className="timeframe-select compact"
                      >
                        {sourceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <img
                        src={Dropdown}
                        alt="dropdown"
                        className="dropdown-icon"
                      />
                    </div>
                  </div>

                  <div className="dropdown-container compact">
                    <div
                      className="custom-select-wrapper compact"
                      onClick={() =>
                        document.getElementById("month-range-select").click()
                      }
                    >
                      <select
                        id="month-range-select"
                        value={monthRange}
                        onChange={(e) => setMonthRange(Number(e.target.value))}
                        className="timeframe-select compact"
                      >
                        {monthOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <img
                        src={Dropdown}
                        alt="dropdown"
                        className="dropdown-icon"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!hasData && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#888",
                    fontSize:
                      screenWidth < 576
                        ? "8px"
                        : screenWidth < 768
                          ? "10px"
                          : "16px",
                    zIndex: 10,
                    backgroundColor: "rgba(255,255,255,0.8)",
                    padding: "10px 20px",
                    borderRadius: "4px",
                  }}
                >
                  No data available for selected period
                </div>
              )}

              <div className="line-chart-canvas">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartLineData}
                    margin={{
                      top: screenWidth < 576 ? 12 : 14,
                      right:
                        screenWidth < 576 ? 16 : screenWidth < 768 ? 20 : 28,
                      left:
                        screenWidth < 576 ? 14 : screenWidth < 768 ? 22 : 36,
                      bottom: screenWidth < 576 ? 16 : 20,
                    }}
                  >
                    <defs>
                      <linearGradient
                        id="colorGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#2F80ED"
                          stopOpacity={0.6}
                        />
                        <stop
                          offset="55%"
                          stopColor="#2F80ED"
                          stopOpacity={0.28}
                        />
                        <stop
                          offset="100%"
                          stopColor="#2F80ED"
                          stopOpacity={0.12}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="x_axis"
                      axisLine={false}
                      tickLine={false}
                      tickMargin={8}
                      interval={0}
                      padding={{
                        left: screenWidth < 576 ? 4 : 8,
                        right: screenWidth < 576 ? 10 : 16,
                      }}
                      tickFormatter={formatMonthLabel}
                      tick={{
                        fontSize:
                          screenWidth < 576 ? 7 : screenWidth < 768 ? 9 : 12,
                        fill: "#111111",
                      }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      width={
                        screenWidth < 576 ? 18 : screenWidth < 768 ? 26 : 42
                      }
                      domain={[0, yAxisMax]}
                      ticks={yAxisTicks}
                      tickFormatter={(value) =>
                        new Intl.NumberFormat().format(value)
                      }
                      tick={{
                        fontSize:
                          screenWidth < 576 ? 7 : screenWidth < 768 ? 9 : 12,
                        fill: "#111111",
                      }}
                      label={<CenteredYAxisLabel />}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="y_axis"
                      stroke="none"
                      fill="url(#colorGradient)"
                      fillOpacity={1}
                      baseValue={0}
                      activeDot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="y_axis"
                      name="y_axis"
                      stroke="#2F80ED"
                      strokeWidth={2}
                      dot={{
                        r: 4,
                        fill: "#ffffff",
                        stroke: "#2F80ED",
                        strokeWidth: 2,
                      }}
                      activeDot={{
                        r: 6,
                        fill: "#ffffff",
                        stroke: "#2F80ED",
                        strokeWidth: 2,
                      }}
                      fillOpacity={1}
                      fill="url(#colorGradient)"
                    />
                    {summary.sales_report &&
                      summary.sales_report.length > 0 &&
                      summary.sales_report[0]?.z_axis && (
                        <Line
                          type="monotone"
                          dataKey="z_axis"
                          name="z_axis"
                          stroke="#1f75a8"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="line-chart-month-label">Month</p>
            </div>

            <div className="chart-card pie-chart">
              <div className="chart-head pie-head">
                <div className="chart-title-wrap">
                  <span className="chart-title-icon revenue-icon">
                    <ChartTitleIcon type="revenue" />
                  </span>
                  <h3 className="chart-main-title">Revenue Distribution</h3>
                </div>
              </div>

              <div className="revenue-content">
                <div className="revenue-top-row">
                  <div className="revenue-pie-wrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart
                        margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
                      >
                        <Pie
                          data={REVENUE_DISTRIBUTION_DATA}
                          cx="50%"
                          cy="50%"
                          innerRadius={
                            screenWidth < 576 ? 32 : screenWidth < 768 ? 44 : 58
                          }
                          outerRadius={
                            screenWidth < 576
                              ? 60
                              : screenWidth < 768
                                ? 78
                                : 100
                          }
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {REVENUE_DISTRIBUTION_DATA.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              stroke="#fff"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>

                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        color: "#2f3d59",
                      }}
                    >
                      <div className="revenue-total-value">Rs.3.2 Cr</div>
                      <div className="revenue-total-label">Total</div>
                    </div>
                  </div>

                  <div className="revenue-legend">
                    {REVENUE_DISTRIBUTION_DATA.map((item) => (
                      <div key={item.name} className="revenue-legend-item">
                        <span
                          className="revenue-legend-dot"
                          style={{ backgroundColor: item.color }}
                        />
                        <span>{`${item.name} (${item.value}%)`}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="revenue-period-select-wrap">
                  <div className="custom-select-wrapper compact small revenue-select-wrapper">
                    <select
                      className="timeframe-select compact small"
                      value={revenuePeriod}
                      onChange={(event) => setRevenuePeriod(event.target.value)}
                    >
                      {revenuePeriodOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <img
                      src={Dropdown}
                      alt="dropdown"
                      className="dropdown-icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOverview;
